import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchSKUCompanies,
  fetchSKUBrands,
  fetchSKUSubbrands,
  fetchSKUDetails as fetchSKUDetailsApi,
  fetchSKUVolumes as fetchSKUVolumesApi,
  fetchSKUPackagingTypes as fetchSKUPackagingTypesApi,
  fetchSKUPackagingSizes as fetchSKUPackagingSizesApi,
} from "../api/marketOverviewApi";

const initialState = {
  // selected sku input values
  skuValues: {
    company: null,
    brand: null,
    brandLabel: null, // Store brand name
    subbrand: null,
    subbrandLabel: null, // Store subbrand name
    volume: null,
    pack_type: null,
    pack_size: null,
  },

  // beverages after selected
  filteredBeverages: [],

  // currently active tab
  activeTabKey: "PET_Performance",

  // Dimension options, kept as plain lists with no caching map
  companies: [],
  brands: [],
  subbrands: [],
  volumes: [],
  packTypes: [],
  packSizes: [],

  // Loading state per dimension
  loading: {
    companies: false,
    brands: false,
    subbrands: false,
    volumes: false,
    packTypes: false,
    packSizes: false,
  },
  // Error per dimension, available for display
  errors: {
    companies: null,
    brands: null,
    subbrands: null,
    volumes: null,
    packTypes: null,
    packSizes: null,
  },
};

// ============== Async thunks: fetch and store results, no caching ==============
export const fetchCompanies = createAsyncThunk(
  "sku/fetchCompanies",
  async ({ country }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (!auth.id_client) throw new Error("missing id_client");
      const data = await fetchSKUCompanies({
        id_client: auth.id_client,
        country,
      });
      return data;
    } catch (e) {
      return rejectWithValue(e.message || "fail fetching companies");
    }
  }
);

export const fetchBrands = createAsyncThunk(
  "sku/fetchBrands",
  async ({ company, country }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (!auth.id_client) throw new Error("missing id_client");
      if (!company) throw new Error("missing company");
      const data = await fetchSKUBrands({
        id_client: auth.id_client,
        company,
        country,
      });
      return data;
    } catch (e) {
      return rejectWithValue(e.message || "missing fetching brands");
    }
  }
);

export const fetchSubbrands = createAsyncThunk(
  "sku/fetchSubbrands",
  async ({ id_brand, country }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (!auth.id_client) throw new Error("missing id_client");
      if (!id_brand) throw new Error("missing fetching id_brand");
      const data = await fetchSKUSubbrands({
        id_client: auth.id_client,
        id_brand,
        country,
      });

      return data;
    } catch (e) {
      return rejectWithValue(e.message || "fail fetching subbrands");
    }
  }
);

export const fetchSKUDetails = createAsyncThunk(
  "sku/fetchDetails",
  async ({ id_subbrand, country }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (!auth.id_client) throw new Error("missing id_client");
      if (!id_subbrand) throw new Error("missing id_subbrand");
      const data = await fetchSKUDetailsApi({
        id_client: auth.id_client,
        id_subbrand,
        country,
      });
      // Expected new structure: { volumes: [], packaging_types: [], packaging_sizes: [] }
      return {
        volumes: Array.isArray(data?.volumes) ? data.volumes : [],
        packTypes: Array.isArray(data?.packaging_types)
          ? data.packaging_types
          : [],
        packSizes: Array.isArray(data?.packaging_sizes)
          ? data.packaging_sizes
          : [],
      };
    } catch (e) {
      return rejectWithValue(e.message || "fail fetching sku details");
    }
  }
);

// Fetch volumes dynamically based on packaging type and size
export const fetchVolumes = createAsyncThunk(
  "sku/fetchVolumes",
  async (
    { id_subbrand, country, packaging_type, packaging_size },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState();
      if (!auth.id_client) throw new Error("missing id_client");
      if (!id_subbrand) throw new Error("missing id_subbrand");
      const data = await fetchSKUVolumesApi({
        id_client: auth.id_client,
        id_subbrand,
        country,
        packaging_type,
        packaging_size,
      });
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return rejectWithValue(e.message || "fail fetching volumes");
    }
  }
);

// Fetch packaging types dynamically based on volume and size
export const fetchPackagingTypes = createAsyncThunk(
  "sku/fetchPackagingTypes",
  async (
    { id_subbrand, country, volume, packaging_size },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState();
      if (!auth.id_client) throw new Error("missing id_client");
      if (!id_subbrand) throw new Error("missing id_subbrand");
      const data = await fetchSKUPackagingTypesApi({
        id_client: auth.id_client,
        id_subbrand,
        country,
        volume,
        packaging_size,
      });
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return rejectWithValue(e.message || "fail fetching packaging types");
    }
  }
);

// Fetch packaging sizes dynamically based on volume and type
export const fetchPackagingSizes = createAsyncThunk(
  "sku/fetchPackagingSizes",
  async (
    { id_subbrand, country, volume, packaging_type },
    { getState, rejectWithValue }
  ) => {
    try {
      const { auth } = getState();
      if (!auth.id_client) throw new Error("missing id_client");
      if (!id_subbrand) throw new Error("missing id_subbrand");
      const data = await fetchSKUPackagingSizesApi({
        id_client: auth.id_client,
        id_subbrand,
        country,
        volume,
        packaging_type,
      });
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return rejectWithValue(e.message || "fail fetching packaging sizes");
    }
  }
);

const skuSlice = createSlice({
  name: "sku",
  initialState,
  reducers: {
    // set sku values
    setSkuValues: (state, action) => {
      let cascade = false;
      let changed = false;
      Object.entries(action.payload).forEach(([k, v]) => {
        if (state.skuValues[k] !== v) {
          state.skuValues[k] = v;
          changed = true;
          if (k === "company" || k === "brand" || k === "subbrand")
            cascade = true;
        }
      });
      if (cascade) {
        // A change upstream invalidates every selection and option list below it
        if (action.payload.company !== undefined) {
          state.skuValues.brand = null;
          state.skuValues.brandLabel = null;
          state.skuValues.subbrand = null;
          state.skuValues.subbrandLabel = null;
          state.skuValues.volume = null;
          state.skuValues.pack_type = null;
          state.skuValues.pack_size = null;
          state.brands = [];
          state.subbrands = [];
          state.volumes = [];
          state.packTypes = [];
          state.packSizes = [];
        } else if (action.payload.brand !== undefined) {
          state.skuValues.subbrand = null;
          state.skuValues.subbrandLabel = null;
          state.skuValues.volume = null;
          state.skuValues.pack_type = null;
          state.skuValues.pack_size = null;
          state.subbrands = [];
          state.volumes = [];
          state.packTypes = [];
          state.packSizes = [];
        } else if (action.payload.subbrand !== undefined) {
          state.skuValues.volume = null;
          state.skuValues.pack_type = null;
          state.skuValues.pack_size = null;
          state.volumes = [];
          state.packTypes = [];
          state.packSizes = [];
        }
      }
      // Handle clearing of volume/pack_type/pack_size
      // When one is cleared, don't clear the others to allow dynamic filtering
      if (action.payload.volume === null && state.skuValues.volume !== null) {
        state.skuValues.volume = null;
      }
      if (
        action.payload.pack_type === null &&
        state.skuValues.pack_type !== null
      ) {
        state.skuValues.pack_type = null;
      }
      if (
        action.payload.pack_size === null &&
        state.skuValues.pack_size !== null
      ) {
        state.skuValues.pack_size = null;
      }
      if (!changed) return;
    },

    // Bulk-set all sku values for edit pre-fill, bypassing the cascade-clear logic
    // in setSkuValues. Also clears dependent option lists so the dropdowns don't
    // briefly render stale options from the previous SKU before fetches resolve.
    prefillSkuValues: (state, action) => {
      state.skuValues = { ...state.skuValues, ...action.payload };
      state.brands = [];
      state.subbrands = [];
      state.volumes = [];
      state.packTypes = [];
      state.packSizes = [];
    },

    // reset selected sku values
    resetSkuValues: (state) => {
      state.skuValues = {
        company: null,
        brand: null,
        brandLabel: null,
        subbrand: null,
        subbrandLabel: null,
        volume: null,
        pack_type: null,
        pack_size: null,
      };
    },

    //set beverages after selected
    setFilteredBeverages: (state, action) => {
      state.filteredBeverages = action.payload;
    },

    // add filterd beverage
    addFilteredBeverage: (state, action) => {
      const newBeverage = action.payload;
      // check if exists
      const exists = state.filteredBeverages.some(
        (bev) => bev.drink.id === newBeverage.drink.id
      );
      if (!exists) {
        state.filteredBeverages.push(newBeverage);
      }
    },

    // remove sku based on ids
    removeSkuById: (state, action) => {
      const skuId = action.payload;

      // remove from filtered beverages
      state.filteredBeverages = state.filteredBeverages.filter(
        (beverage) => beverage.drink.id !== skuId
      );
    },

    // set active key
    setActiveTabKey: (state, action) => {
      state.activeTabKey = action.payload;
    },

    // reset all sku state
    resetSkuState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // companies
      .addCase(fetchCompanies.pending, (state) => {
        state.loading.companies = true;
        state.errors.companies = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loading.companies = false;
        state.companies = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loading.companies = false;
        state.errors.companies = action.payload || action.error.message;
      })
      // brands
      .addCase(fetchBrands.pending, (state) => {
        state.loading.brands = true;
        state.errors.brands = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.loading.brands = false;
        state.brands = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.loading.brands = false;
        state.errors.brands = action.payload || action.error.message;
      })
      // subbrands
      .addCase(fetchSubbrands.pending, (state) => {
        state.loading.subbrands = true;
        state.errors.subbrands = null;
      })
      .addCase(fetchSubbrands.fulfilled, (state, action) => {
        state.loading.subbrands = false;
        state.subbrands = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchSubbrands.rejected, (state, action) => {
        state.loading.subbrands = false;
        state.errors.subbrands = action.payload || action.error.message;
      })
      // unified sku details
      .addCase(fetchSKUDetails.pending, (state) => {
        state.loading.volumes = true;
        state.loading.packTypes = true;
        state.loading.packSizes = true;
        state.errors.volumes = null;
        state.errors.packTypes = null;
        state.errors.packSizes = null;
        // clear previous when new subbrand chosen
        state.volumes = [];
        state.packTypes = [];
        state.packSizes = [];
      })
      .addCase(fetchSKUDetails.fulfilled, (state, action) => {
        state.loading.volumes = false;
        state.loading.packTypes = false;
        state.loading.packSizes = false;
        state.volumes = action.payload.volumes;
        state.packTypes = action.payload.packTypes;
        state.packSizes = action.payload.packSizes;
      })
      .addCase(fetchSKUDetails.rejected, (state, action) => {
        state.loading.volumes = false;
        state.loading.packTypes = false;
        state.loading.packSizes = false;
        state.errors.volumes = action.payload || action.error.message;
        state.errors.packTypes = action.payload || action.error.message;
        state.errors.packSizes = action.payload || action.error.message;
      })
      // dynamic volumes
      .addCase(fetchVolumes.pending, (state) => {
        state.loading.volumes = true;
        state.errors.volumes = null;
      })
      .addCase(fetchVolumes.fulfilled, (state, action) => {
        state.loading.volumes = false;
        state.volumes = action.payload;
      })
      .addCase(fetchVolumes.rejected, (state, action) => {
        state.loading.volumes = false;
        state.errors.volumes = action.payload || action.error.message;
      })
      // dynamic packaging types
      .addCase(fetchPackagingTypes.pending, (state) => {
        state.loading.packTypes = true;
        state.errors.packTypes = null;
      })
      .addCase(fetchPackagingTypes.fulfilled, (state, action) => {
        state.loading.packTypes = false;
        state.packTypes = action.payload;
      })
      .addCase(fetchPackagingTypes.rejected, (state, action) => {
        state.loading.packTypes = false;
        state.errors.packTypes = action.payload || action.error.message;
      })
      // dynamic packaging sizes
      .addCase(fetchPackagingSizes.pending, (state) => {
        state.loading.packSizes = true;
        state.errors.packSizes = null;
      })
      .addCase(fetchPackagingSizes.fulfilled, (state, action) => {
        state.loading.packSizes = false;
        state.packSizes = action.payload;
      })
      .addCase(fetchPackagingSizes.rejected, (state, action) => {
        state.loading.packSizes = false;
        state.errors.packSizes = action.payload || action.error.message;
      });
  },
});

export const {
  setSkuValues,
  prefillSkuValues,
  resetSkuValues,
  setFilteredBeverages,
  addFilteredBeverage,
  removeSkuById,
  setActiveTabKey,
  resetSkuState,
} = skuSlice.actions;

export default skuSlice.reducer;
